import { useState } from "react";
import { heroBackground, siteMeta, stats, categories } from "@/mocks/site";

interface HeroSectionProps {
  keyword: string;
  onKeywordChange: (value: string) => void;
  onSearch: () => void;
  onPickCategory: (key: string) => void;
}

export default function HeroSection({ keyword, onKeywordChange, onSearch, onPickCategory }: HeroSectionProps) {
  const [local, setLocal] = useState(keyword);

  const submit = () => {
    onKeywordChange(local.trim());
    onSearch();
  };

  return (
    <section id="top" className="relative w-full overflow-hidden">
      <div className="absolute inset-0">
        <img
          src={heroBackground}
          alt="避雷档案封面：暗红与琥珀色调的纸质档案拼贴抽象背景"
          title="避雷档案｜国产消费避坑情报站"
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary-950/90 via-foreground-950/80 to-primary-900/75"></div>
        <div className="absolute inset-0 opacity-[0.14] [background-image:linear-gradient(rgba(255,255,255,.6)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.6)_1px,transparent_1px)] [background-size:56px_56px]"></div>
      </div>

      <div className="relative flex min-h-[660px] w-full flex-col justify-center px-4 pb-16 pt-28 md:min-h-[760px] md:px-8 md:pb-20 md:pt-32">
        <div className="mx-auto w-full max-w-6xl">
          <span className="inline-flex items-center gap-2 rounded-full border border-accent-400/50 bg-accent-500/15 px-4 py-1.5 font-label text-xs tracking-wide text-accent-100">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-accent-400 opacity-75"></span>
              <span className="relative inline-flex h-2 w-2 rounded-full bg-accent-400"></span>
            </span>
            {siteMeta.updateNote}
          </span>

          <h2 className="mt-6 max-w-3xl font-heading text-3xl font-black leading-tight text-background-50 sm:text-4xl md:text-5xl lg:text-6xl">
            买前先翻档案，
            <span className="text-accent-400">少踩商家挖好的坑</span>
          </h2>

          <p className="mt-5 max-w-2xl text-sm leading-relaxed text-background-50/80 md:text-base">
            {siteMeta.intro}
          </p>

          <div className="mt-8 flex w-full max-w-2xl flex-col gap-3 sm:flex-row">
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-50/25 bg-background-50/10 px-4 py-3 backdrop-blur">
              <i className="ri-search-2-line text-lg leading-none text-background-50/70"></i>
              <input
                type="search"
                value={local}
                onChange={(e) => setLocal(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submit();
                }}
                placeholder="搜索品牌或问题关键词，如 保修 / 材质 / 退货"
                aria-label="搜索避雷档案"
                className="w-full bg-transparent text-sm text-background-50 placeholder:text-background-50/50 focus:outline-none"
              />
            </div>
            <button
              type="button"
              onClick={submit}
              className="flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 font-label text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-focus-3-line text-base leading-none"></i>
              查档案
            </button>
          </div>

          <div className="mt-5 flex flex-wrap items-center gap-2">
            <span className="font-label text-xs text-background-50/60">快速定位：</span>
            {categories.map((cat) => (
              <button
                key={cat.key}
                type="button"
                onClick={() => onPickCategory(cat.key)}
                className="cursor-pointer whitespace-nowrap rounded-full border border-background-50/25 px-3.5 py-1.5 font-label text-xs text-background-50/85 transition-colors hover:border-accent-400 hover:text-accent-300"
              >
                {cat.label}
              </button>
            ))}
          </div>

          <div className="mt-12 grid w-full grid-cols-2 gap-3 md:grid-cols-4 md:gap-4">
            {stats.map((item) => (
              <div
                key={item.id}
                className="rounded-lg border border-background-50/15 bg-background-50/10 px-4 py-4 backdrop-blur"
              >
                <i className={`${item.icon} text-xl leading-none text-accent-400`}></i>
                <div className="mt-3 font-heading text-2xl font-bold text-background-50 md:text-3xl">
                  {item.value}
                  <span className="ml-1 text-sm font-normal text-background-50/70">{item.suffix}</span>
                </div>
                <span className="mt-1 block font-label text-xs text-background-50/70">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}